import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SitewideTasks from './pages/SitewideTasks';
import PageTasks from './pages/PageTasks';
import WeeklyPlan from './pages/WeeklyPlan';
import Export from './pages/Export';
import Settings from './pages/Settings';
import Notes from './pages/Notes';
import DeletedTasks from './pages/DeletedTasks';

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if ((user as any)?.role === 'member') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-navy-900">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-navy-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Mobile warning overlay — uses screen.width so it works on real devices
// but doesn't block narrow preview panels (where screen.width is still desktop)
function MobileWarning() {
  const [isNarrow, setIsNarrow] = React.useState(false);

  React.useEffect(() => {
    const check = () => setIsNarrow(window.screen.width < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  if (!isNarrow) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-navy-900 flex flex-col items-center justify-center p-8 text-center">
      <div className="text-6xl mb-6">🖥️</div>
      <h2 className="text-2xl font-bold text-white mb-3">Desktop Required</h2>
      <p className="text-navy-300 text-base max-w-sm">
        The Tkxel SEO Dashboard is optimized for desktop use. Please open this application on a screen wider than 1024px.
      </p>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <MobileWarning />
      <div>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="sitewide-tasks" element={<SitewideTasks />} />
            <Route path="page-tasks" element={<PageTasks />} />
            <Route path="weekly-plan" element={<WeeklyPlan />} />
            <Route path="export" element={<Export />} />
            <Route path="deleted-tasks" element={<AdminRoute><DeletedTasks /></AdminRoute>} />
            <Route path="settings" element={<AdminRoute><Settings /></AdminRoute>} />
            <Route path="notes" element={<AdminRoute><Notes /></AdminRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}
